import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updatePaymentDetails } from '../actions/checkout-subscription-actions';
import { fetchAuthenticatedUser } from '../actions';
import { pollForSubscriptionStatus } from '../components/BillingForm/helpers';

/**
 * Hook that manages payment method update + polling for past-due annual subscribers.
 *
 * Flow: updatePaymentDetails (saves new card) → poll fetchAuthenticatedUser
 * until hasPastDueSubscription === false (Stripe retried the failed invoice).
 *
 * If polling exhausts, the payment method was saved but recovery hasn't confirmed yet.
 * The caller can show a refresh CTA that re-checks via handleRefresh().
 *
 * Uses a single `phase` state to avoid render gaps between multiple boolean flags.
 * Phases: idle → submitting → polling → recovered | exhausted (→ polling on refresh)
 */

const PHASES = {
    IDLE: 'idle',
    SUBMITTING: 'submitting',
    POLLING: 'polling',
    RECOVERED: 'recovered',
    EXHAUSTED: 'exhausted',
};

const usePaymentUpdate = () => {
    const dispatch = useDispatch();
    const subscriptions = useSelector((state) => state.subscribe?.subscriptions);

    const [phase, setPhase] = useState(PHASES.IDLE);
    const [error, setError] = useState('');

    const getSubscriptionId = () => {
        const pastDueSub = subscriptions?.find((sub) => sub.isPastDue);
        return pastDueSub?.id || null;
    };

    const handleUpdatePayment = useCallback(async (paymentMethodId) => {
        const subscriptionId = getSubscriptionId();
        if (!subscriptionId || !paymentMethodId) {
            setError('Unable to update payment details. Please try again.');
            return { recovered: false };
        }

        setPhase(PHASES.SUBMITTING);
        setError('');

        try {
            await dispatch(updatePaymentDetails({ subscriptionId, paymentMethodId })); // NOSONAR — Redux thunk returns a promise

            // Payment method saved — now poll for Stripe to recover the invoice
            setPhase(PHASES.POLLING);

            const recovered = await pollForSubscriptionStatus({
                fetchUser: () => dispatch(fetchAuthenticatedUser()),
                expectPastDue: false,
                maxRetries: 6,
                retryDelayMs: 5000,
            });

            if (recovered) {
                setPhase(PHASES.RECOVERED);
                return { recovered: true };
            }

            setPhase(PHASES.EXHAUSTED);
            return { recovered: false };
        } catch (err) {
            const message = err?.response?.data?.DisplayMessage ||
                err?.response?.data?.Message ||
                err?.message ||
                'Something went wrong. Please try again.';
            setError(message);
            setPhase(PHASES.IDLE);
            return { recovered: false, error: message };
        }
    }, [dispatch, subscriptions]);

    const handleRefresh = useCallback(async () => {
        setPhase(PHASES.POLLING);
        setError('');

        try {
            const recovered = await pollForSubscriptionStatus({
                fetchUser: () => dispatch(fetchAuthenticatedUser()),
                expectPastDue: false,
                maxRetries: 6,
                retryDelayMs: 5000,
            });

            if (recovered) {
                setPhase(PHASES.RECOVERED);
                return { recovered: true };
            }

            setPhase(PHASES.EXHAUSTED);
            return { recovered: false };
        } catch (_err) { // NOSONAR — error is intentionally swallowed; user sees exhausted state
            setPhase(PHASES.EXHAUSTED);
            return { recovered: false };
        }
    }, [dispatch]);

    const clearError = () => {
        setError('');
    };

    return {
        submitting: phase === PHASES.SUBMITTING,
        isPolling: phase === PHASES.POLLING,
        pollExhausted: phase === PHASES.EXHAUSTED,
        recovered: phase === PHASES.RECOVERED,
        error,
        handleUpdatePayment,
        handleRefresh,
        clearError,
    };
};

export default usePaymentUpdate;
